import { currentLanguage, localizedText, useLanguage, type Language } from './i18n';
import { useMemo, useSyncExternalStore } from 'react';

export type RouteId =
  | 'home'
  | 'team'
  | 'security'
  | 'keys'
  | 'models'
  | 'usage'
  | 'history'
  | 'support'
  | 'endpoints'
  | 'endpointDetail'
  | 'login';

export type Route = {
  id: RouteId;
  title: string;
  path: string;
};

type RouteLocation = Pick<Location, 'pathname'>;

export function routeFromLocation(
  location: RouteLocation = window.location,
  language: Language = currentLanguage(),
): Route {
  const pathname = location.pathname;
  const title = (chinese: string, english: string) => localizedText(language, chinese, english);
  if (pathname.startsWith('/login'))
    return { id: 'login', title: title('登录', 'Login'), path: pathname };
  if (pathname.startsWith('/team'))
    return { id: 'team', title: title('组织', 'Organization'), path: pathname };
  if (pathname.startsWith('/security'))
    return { id: 'security', title: title('安全', 'Security'), path: pathname };
  if (pathname.startsWith('/keys'))
    return { id: 'keys', title: title('我的 API 密钥', 'My API Keys'), path: pathname };
  if (pathname.startsWith('/models'))
    return { id: 'models', title: title('可用模型', 'Available Models'), path: pathname };
  if (pathname.startsWith('/request-history'))
    return { id: 'history', title: title('请求记录', 'Request History'), path: pathname };
  // Billing support is a topic of the Support page; old links still resolve.
  if (pathname.startsWith('/support') || pathname.startsWith('/billing-support'))
    return { id: 'support', title: title('支持', 'Support'), path: pathname };
  if (pathname.startsWith('/usage'))
    return { id: 'usage', title: title('用量与账单', 'Usage & Billing'), path: pathname };
  if (pathname.startsWith('/endpoints'))
    return { id: 'endpoints', title: title('端点', 'Endpoints'), path: pathname };
  if (pathname.startsWith('/endpoint_detail')) {
    return { id: 'endpointDetail', title: title('端点', 'Endpoint'), path: pathname };
  }
  return { id: 'home', title: localizedText(language, '概览', 'Overview', '概要'), path: '/' };
}

export function navigate(href: string) {
  const from = `${window.location.pathname}${window.location.search}`;
  window.history.pushState({ portal: true, from }, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function subscribeLocation(listener: () => void) {
  window.addEventListener('popstate', listener);
  return () => window.removeEventListener('popstate', listener);
}

function locationSnapshot() {
  return window.location.href;
}

export function useRoute(): Route {
  const language = useLanguage();
  const href = useSyncExternalStore(subscribeLocation, locationSnapshot, () => 'http://localhost/');
  return useMemo(() => routeFromLocation(new URL(href), language), [href, language]);
}
